/**
 * Presenting how much it matters if something fails.
 *
 * The server decides the number and where it came from; this file decides how
 * it reads. The two things worth carrying to the screen are the band — "High"
 * means something to a planner, "7.0" does not — and the provenance, because a
 * figure somebody set for this asset and a figure inherited from its class are
 * different claims and should not look identical.
 *
 * NOT_ASSESSED is a first-class state here, not a blank. An asset nobody has
 * judged is not low-consequence; it is a decision waiting to be made, and it
 * should look like one.
 */

export type CriticalitySource = 'SET_BY_USER' | 'FROM_ASSET_CLASS' | 'NOT_ASSESSED';
export type CriticalityBand = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'NOT_ASSESSED';

export type Criticality = {
  score: number | null;
  band: CriticalityBand;
  source: CriticalitySource;
  provenance: string;
  note?: string | null;
};

export const BAND_STYLE: Record<CriticalityBand, { label: string; className: string }> = {
  CRITICAL: {
    label: 'Critical',
    className: 'bg-status-destructive-subtle text-status-destructive-foreground',
  },
  HIGH: { label: 'High', className: 'bg-status-warning-subtle text-status-warning-foreground' },
  MEDIUM: { label: 'Medium', className: 'bg-status-info-subtle text-status-info-foreground' },
  LOW: { label: 'Low', className: 'bg-status-neutral-subtle text-status-neutral-foreground' },
  // Deliberately not a muted grey. An unmade decision is something to act on,
  // and styling it as absence is how it stays unmade.
  NOT_ASSESSED: {
    label: 'Not assessed',
    className: 'border border-dashed border-status-warning text-status-warning-foreground',
  },
};

/** Short form for a table cell: the band, with the number for ordering. */
export function criticalityLabel(c: Criticality | null | undefined): string {
  if (!c || c.band === 'NOT_ASSESSED') return 'Not assessed';
  return `${BAND_STYLE[c.band].label}${c.score != null ? ` · ${c.score}` : ''}`;
}

/** How the figure was arrived at, in a few words, for a tooltip or caption. */
export function sourceLabel(source: CriticalitySource): string {
  switch (source) {
    case 'SET_BY_USER':
      return 'Judged for this record';
    case 'FROM_ASSET_CLASS':
      return 'Inherited from its class';
    default:
      return 'Nobody has decided';
  }
}
